import type { ReactNode } from 'react'
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'

interface NavigationContextType {
  // Primary navigation state
  primaryNavCollapsed: boolean
  setPrimaryNavCollapsed: (collapsed: boolean) => void

  // Secondary navigation state
  secondaryNavOpen: boolean
  setSecondaryNavOpen: (open: boolean) => void
  secondaryNavLocked: boolean
  setSecondaryNavLocked: (locked: boolean) => void

  // Drawer hover state for primary nav
  primaryDrawerHovered: boolean
  setPrimaryDrawerHovered: (hovered: boolean) => void
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
)

const STORAGE_KEY = 'day-navigation-state'

interface NavigationProviderProps {
  children: ReactNode
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
}) => {
  // Load initial state from localStorage or use defaults
  const [primaryNavCollapsed, setPrimaryNavCollapsedState] = useState<boolean>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          return parsed.primaryNavCollapsed ?? false
        } catch {
          return false
        }
      }
      // Default to open (not collapsed) for first-time users
      return false
    }
  )

  const [secondaryNavOpen, setSecondaryNavOpen] = useState(false)
  const [secondaryNavLocked, setSecondaryNavLocked] = useState(false)
  const [primaryDrawerHovered, setPrimaryDrawerHovered] = useState(false)

  // Save navigation state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      primaryNavCollapsed,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [primaryNavCollapsed])

  // Enhanced setter that handles localStorage persistence
  const setPrimaryNavCollapsed = useCallback((collapsed: boolean) => {
    setPrimaryNavCollapsedState(collapsed)
  }, [])

  const value: NavigationContextType = {
    primaryNavCollapsed,
    setPrimaryNavCollapsed,
    secondaryNavOpen,
    setSecondaryNavOpen,
    secondaryNavLocked,
    setSecondaryNavLocked,
    primaryDrawerHovered,
    setPrimaryDrawerHovered,
  }

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider')
  }
  return context
}

// Utility hook to calculate effective navigation width
export const useNavigationWidth = () => {
  const { primaryNavCollapsed, secondaryNavLocked } = useNavigation()

  const COLLAPSED_WIDTH = 32
  const EXPANDED_WIDTH = 240
  const SECONDARY_NAV_WIDTH = 280

  const primaryWidth = primaryNavCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH
  const secondaryWidth = secondaryNavLocked ? SECONDARY_NAV_WIDTH : 0

  return {
    primaryWidth,
    secondaryWidth,
    totalWidth: primaryWidth + secondaryWidth,
  }
}
